/**
 * Documents Page
 * Document management across all entities
 */

import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Download,
  Trash2,
  Search,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Upload,
  Pencil,
  History,
} from 'lucide-react';
import { DocumentCategory } from '@bizsolutions/shared';

interface Document {
  id: string;
  accountId: string | null;
  contactId: string | null;
  dealId: string | null;
  ticketId: string | null;
  invoiceId: string | null;
  contractId: string | null;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  category: string | null;
  tags: string[];
  description: string | null;
  version: number;
  parentDocumentId: string | null;
  isLatestVersion: boolean;
  uploaderName: string;
  createdAt: string;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
  if (mimeType.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
  if (mimeType.includes('word') || mimeType.includes('document'))
    return <FileText className="h-4 w-4 text-blue-700" />;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet'))
    return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
  return <File className="h-4 w-4 text-gray-500" />;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const CATEGORY_LABELS: Record<string, string> = {
  [DocumentCategory.CONTRACT]: 'Contract',
  [DocumentCategory.INVOICE]: 'Invoice',
  [DocumentCategory.PROPOSAL]: 'Proposal',
  [DocumentCategory.REPORT]: 'Report',
  [DocumentCategory.CORRESPONDENCE]: 'Correspondence',
  [DocumentCategory.LEGAL]: 'Legal',
  [DocumentCategory.FINANCIAL]: 'Financial',
  [DocumentCategory.OTHER]: 'Other',
};

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [versionDocId, setVersionDocId] = useState<string | null>(null);
  const [uploadMeta, setUploadMeta] = useState({
    accountId: '',
    category: '',
    description: '',
  });

  // Fetch documents
  const { data: docsData } = useQuery({
    queryKey: ['documents', search, categoryFilter, page],
    queryFn: () =>
      api.getDocuments({
        search: search || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        page,
        limit: 20,
      }),
  });
  const documents = (docsData?.data || []) as Document[];
  const pagination = docsData?.pagination;

  // Fetch accounts for upload form
  const { data: accountsData } = useQuery({
    queryKey: ['accountsList'],
    queryFn: () => api.getAccounts({ limit: 100 }),
  });
  const accounts = (accountsData?.data || []) as { id: string; name: string }[];

  // Fetch versions
  const { data: versionsData } = useQuery({
    queryKey: ['documentVersions', versionDocId],
    queryFn: () => api.getDocumentVersions(versionDocId!),
    enabled: !!versionDocId,
  });
  const versions = (versionsData?.data || []) as Document[];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, metadata }: { file: File; metadata: Record<string, string | undefined> }) =>
      api.uploadDocument(file, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploadMeta({ accountId: '', category: '', description: '' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateDocument(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setIsEditOpen(false);
      setEditingDoc(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDocument(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  });

  // Version upload mutation
  const versionMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => api.uploadDocumentVersion(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documentVersions'] });
    },
  });

  function handleUpload() {
    if (!selectedFile || !uploadMeta.accountId) return;
    uploadMutation.mutate({
      file: selectedFile,
      metadata: {
        accountId: uploadMeta.accountId,
        category: uploadMeta.category || undefined,
        description: uploadMeta.description || undefined,
      },
    });
  }

  function handleDownload(doc: Document) {
    const url = api.getDocumentDownloadUrl(doc.id);
    const token = api.getAccessToken();
    // Use fetch with auth header for download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = doc.originalName;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  function handleVersionUpload(docId: string) {
    setVersionDocId(docId);
    versionFileInputRef.current?.click();
  }

  function onVersionFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file && versionDocId) {
      versionMutation.mutate({ id: versionDocId, file });
    }
    e.target.value = '';
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Upload, manage, and organize documents</p>
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Hidden file input for version uploads */}
      <input
        ref={versionFileInputRef}
        type="file"
        className="hidden"
        onChange={onVersionFileSelected}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8"
          />
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(DocumentCategory).map(([key, val]) => (
              <SelectItem key={key} value={val}>
                {CATEGORY_LABELS[val] || val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Uploaded By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getFileIcon(doc.mimeType)}
                    <div>
                      <div className="font-medium text-sm">{doc.originalName}</div>
                      {doc.description && (
                        <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                          {doc.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {doc.category ? (
                    <Badge variant="outline">{CATEGORY_LABELS[doc.category] || doc.category}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{formatFileSize(doc.fileSize)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">v{doc.version}</Badge>
                </TableCell>
                <TableCell className="text-sm">{doc.uploaderName}</TableCell>
                <TableCell className="text-sm">
                  {new Date(doc.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} title="Download">
                      <Download className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingDoc(doc);
                        setIsEditOpen(true);
                      }}
                      title="Edit metadata"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVersionUpload(doc.id)}
                      title="Upload new version"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setVersionDocId(doc.parentDocumentId || doc.id);
                        setIsVersionsOpen(true);
                      }}
                      title="Version history"
                    >
                      <History className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Delete this document?')) deleteMutation.mutate(doc.id);
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  No documents found. Upload your first document.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File</Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  {selectedFile ? selectedFile.name : 'Choose File'}
                </Button>
                {selectedFile && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </span>
                )}
              </div>
            </div>
            <div>
              <Label>Account</Label>
              <Select value={uploadMeta.accountId} onValueChange={(v) => setUploadMeta({ ...uploadMeta, accountId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={uploadMeta.category} onValueChange={(v) => setUploadMeta({ ...uploadMeta, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DocumentCategory).map(([key, val]) => (
                    <SelectItem key={key} value={val}>
                      {CATEGORY_LABELS[val] || val}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={uploadMeta.description}
                onChange={(e) => setUploadMeta({ ...uploadMeta, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!selectedFile || !uploadMeta.accountId || uploadMutation.isPending}>
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Metadata Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          {editingDoc && (
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={editingDoc.category || ''}
                  onValueChange={(v) => setEditingDoc({ ...editingDoc, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DocumentCategory).map(([key, val]) => (
                      <SelectItem key={key} value={val}>
                        {CATEGORY_LABELS[val] || val}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={editingDoc.description || ''}
                  onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (editingDoc)
                  updateMutation.mutate({
                    id: editingDoc.id,
                    data: { category: editingDoc.category, description: editingDoc.description },
                  });
              }}
              disabled={updateMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={isVersionsOpen} onOpenChange={setIsVersionsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  {getFileIcon(v.mimeType)}
                  <div>
                    <div className="text-sm font-medium">Version {v.version}</div>
                    <div className="text-xs text-muted-foreground">
                      {v.uploaderName} - {new Date(v.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {v.isLatestVersion && <Badge>Current</Badge>}
                  <span className="text-xs text-muted-foreground">{formatFileSize(v.fileSize)}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(v)}>
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
            {versions.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No version history.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
